import {useLayoutEffect ,useRef, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import Timeline from "./Timeline";
import SegmentEditor from "./SegmentEditor";
import React from "react";
import { useVideoPlayer } from "../../hook/useVideoPlayer";
import { useEdits } from "../../hook/useEdits";
import { useVideoProcessing } from "../../hook/useVideoProcessing";
import { useVideoSource } from "../../hook/useVideoSource";
import { generateFullTimeline } from "../../utils/videoEditor/util";
import { useTimelineInteraction } from "../../hook/useTimelineInteraction";
import DraggableLogo from "./DraggableLogo";

const FPS = 30;

export default function VideoEditor({ medium, logoUrl }: { medium: any, logoUrl: any }) {
  const intl = useIntl();
  const [sourceUrl, setSourceUrl] = useState(medium.url);
  const [aspect, setAspect] = useState<string>("16/9");
  const { duration } = useVideoSource(sourceUrl);
  const [showLogo, setShowLogo] = useState(false);
  const SIZE_PRESETS = [40, 60, 80, 100, 120];
  const [logoSizeIdx, setLogoSizeIdx] = useState(2);
  const logoSize = SIZE_PRESETS[logoSizeIdx];
  const [logoPosition, setLogoPosition] = useState({ x: 10, y: 10 }); 

  const videoContainerRef = useRef<HTMLDivElement>(null);

  const {
    edits,
    setEdits,
    selectedIdx,
    setSelectedIdx,
    timelineZoom,
    setTimelineZoom,
    addSegment,
    updateEdit,
    removeEdit,
  } = useEdits(duration);
  
  const {
    videoRef,
    playing,
    current,
    setCurrent,
    togglePlay,
    seekTo
  } = useVideoPlayer();
    
  const {
    timelineRef,
    secToPx,
    goToFrame,
    startDrag,
  } = useTimelineInteraction(
    duration,
    FPS,
    edits,
    setEdits,
    setCurrent,
    videoRef
  );
  const timeline = generateFullTimeline(edits, duration);
  const [timelineRefresh, setTimelineRefresh] = useState(0);

  const { loading, resultUrl, handleProcess, setResultUrl } = useVideoProcessing(showLogo ? logoUrl.picture.logo.url : null);
  useLayoutEffect(() => {
    if (timelineRef.current && timelineRef.current.offsetWidth > 0) {
      setTimelineRefresh((r) => r + 1);
    }
  }, [timelineRef.current]);

  useEffect(() => {
    setTimelineRefresh((r) => r + 1);
  }, [timelineZoom]);

  useEffect(() => {
    if (!timelineRef.current) return;
    const ref = timelineRef.current;
    const resizeObs = new window.ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.contentRect.width > 0) {
          setTimelineRefresh(r => r + 1); // Juste un refresh dummy
        }
      }
    });
    resizeObs.observe(ref);
    return () => resizeObs.disconnect();
  }, [timelineRef.current]);

  useEffect(() => {
    setSourceUrl(medium.url);
    setSelectedIdx(null);
    setEdits([]);         
    setTimelineZoom(1);  
    setTimelineRefresh(0);
    setResultUrl("");     
  }, [medium.url]);

  useEffect(() => {
  const vid = videoRef.current!;
  const onMeta = () => {
    setAspect(`${vid.videoWidth}/${vid.videoHeight}`);
  };
  vid.addEventListener("loadedmetadata", onMeta);
  return () => vid.removeEventListener("loadedmetadata", onMeta);
}, [sourceUrl]);

  
  return (
    <div className="max-w-4xl mx-auto font-sans bg-brand_black rounded-2xl shadow-xl p-4 sm:p-8">
        <a
            className="btn-brand-primary mb-4"
            href={sourceUrl}
            target="_blank"
          >
              {intl.formatMessage({ id: "video_editor.open_new_tab", defaultMessage: "Ouvrir la vidéo dans un nouvel onglet" })}
        </a>
      <div className="mb-8">
        <div
          ref={videoContainerRef}
          className="video-container relative rounded-xl shadow-lg border border-brand_200">
          <video
            ref={videoRef}
            src={sourceUrl}
            style={{ aspectRatio: aspect }}
            className="w-full h-full object-contain"
            preload="metadata"
            onTimeUpdate={e => setCurrent((e.target as HTMLVideoElement).currentTime)}
          />
          {showLogo && (
            <DraggableLogo
              logoUrl={logoUrl.picture.logo.url}
              position={logoPosition}
              setPosition={setLogoPosition}
              size={logoSize}
            />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3 mb-2">
          <button className="btn-brand-primary" onClick={togglePlay}>
            {playing ? intl.formatMessage({ id: "video_editor.pause", defaultMessage: "Pause" }) : intl.formatMessage({ id: "video_editor.play", defaultMessage: "Play" })}
          </button>
          <button className="btn-brand-primary" onClick={addSegment}>
            {intl.formatMessage({ id: "video_editor.add_effect", defaultMessage: "Ajouter un effet" })}
          </button>
          <span className="text-sm text-brand_gray w-full sm:w-auto">
            Frame: {Math.round(current * FPS)} / {Math.round(duration * FPS)} | t = {current.toFixed(2)}s
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <button
            className="btn-brand-primary"
            onClick={() => setShowLogo((s) => !s)}
          >
            {showLogo ? intl.formatMessage({ id: "video_editor.remove_logo", defaultMessage: "Retirer le logo" }) : intl.formatMessage({ id: "video_editor.add_logo", defaultMessage: "Ajouter un logo" })}
          </button>
          {showLogo && (
            <div className="flex items-center gap-1">
              {SIZE_PRESETS.map((_, idx) => (
                <button
                  key={idx}
                  className={`px-2 py-1 rounded ${
                    idx === logoSizeIdx ? "bg-brand_main text-white" : "bg-brand_200 text-brand_main"
                  }`}
                  onClick={() => setLogoSizeIdx(idx)}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <Timeline
        key={timelineZoom}
        duration={duration}
        edits={edits}
        ref={timelineRef}
        seekTo={seekTo}
        current={current}
        setSelectedIdx={setSelectedIdx}
        selectedIdx={selectedIdx}
        FPS={FPS}
        zoom={timelineZoom}
        setZoom={setTimelineZoom}
        secToPx={secToPx}
        startDrag={startDrag}
        refresh={timelineRefresh}
      />
      {selectedIdx !== null && edits[selectedIdx] && (
        <SegmentEditor
          seg={edits[selectedIdx]}
          idx={selectedIdx}
          FPS={FPS}
          edits={edits}
          duration={duration}
          updateEdit={updateEdit}
          removeEdit={removeEdit}
          goToFrame={goToFrame}
        />
      )}
      <button
        className="btn-brand-primary mt-8"
        onClick={() => {
          if (!videoRef.current || !videoContainerRef.current) {
            console.log("not found");
            return;
          };

          const vid = videoRef.current;
          const vidRect = vid.getBoundingClientRect();
          const contRect = videoContainerRef.current.getBoundingClientRect();

          // ratio réel de la vidéo
          const videoAR = vid.videoWidth / vid.videoHeight;
          // ratio de l'élément <video> à l'écran
          const contAR  = vidRect.width  / vidRect.height;

          // on isole la width/height _sans_ les bandes noires
          let contentW: number, contentH: number;
          if (contAR > videoAR) {
            // bandes à gauche / droite
            contentH = vidRect.height;
            contentW = contentH * videoAR;
          } else {
            // bandes en haut / bas
            contentW = vidRect.width;
            contentH = contentW / videoAR;
          }
        
          // on scale par rapport à la vraie largeur vidéo
          const scaleFactor   = vid.videoWidth / contentW;
          const realLogoSize  = Math.round(logoSize * scaleFactor);
        
          handleProcess(
            timeline,
            sourceUrl,
            logoPosition,
            realLogoSize
          );
        }}
        disabled={loading || timeline.length === 0}
      >
        {loading ? intl.formatMessage({ id: "video_editor.processing", defaultMessage: "Traitement…" }) : intl.formatMessage({ id: "video_editor.generate", defaultMessage: "Générer la vidéo" })}
      </button>

      {resultUrl && (
        <div className="mt-10">
          <h2 className="text-lg font-bold mb-4 text-brand_main">{intl.formatMessage({ id: "video_editor.result", defaultMessage: "Résultat" })}</h2>
            <div className="video-container rounded-xl shadow-lg border border-brand_200">
              <video src={resultUrl}
                controls
                className="w-full h-full object-contain"
              />
            </div>
        </div>
      )}
    </div>
  );
}